import React, { useState, useEffect } from 'react';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { FaTrash, FaPlus } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../complaints/ComplaintManagement.css';

const StaffComplaintMapping = ({ onBack }) => {
  const location = useLocation();
  const [staffTypes, setStaffTypes] = useState([]);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [selectedStaffType, setSelectedStaffType] = useState(null);
  const [selectedComplaintTypes, setSelectedComplaintTypes] = useState([]);
  const [availableComplaintTypes, setAvailableComplaintTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [staffTypesRes, complaintTypesRes] = await Promise.all([
        apiClient.get('/staff/types'),
        apiClient.get('/complaints/types')
      ]);
      
      setStaffTypes(staffTypesRes.data);
      setComplaintTypes(complaintTypesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toastError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffTypeSelect = (staffType) => {
    setSelectedStaffType(staffType);
    
    // Load existing complaint mappings for this staff type
    const existingComplaintIds = staffType.complaints || [];
    const mappedComplaintTypes = complaintTypes.filter(ct => 
      existingComplaintIds.includes(ct.type_id)
    );
    
    setSelectedComplaintTypes(mappedComplaintTypes);
    
    // Set available complaint types (not yet mapped)
    const availableTypes = complaintTypes.filter(ct => 
      !existingComplaintIds.includes(ct.type_id)
    );
    setAvailableComplaintTypes(availableTypes);
  };

  const handleAddComplaintType = (complaintType) => {
    if (!selectedStaffType) return;
    
    // Add to selected complaint types
    setSelectedComplaintTypes(prev => [...prev, complaintType]);
    
    // Remove from available complaint types
    setAvailableComplaintTypes(prev => 
      prev.filter(ct => ct.type_id !== complaintType.type_id)
    );
  };

  const handleRemoveComplaintType = (complaintType) => {
    // Remove from selected complaint types
    setSelectedComplaintTypes(prev => 
      prev.filter(ct => ct.type_id !== complaintType.type_id)
    );
    
    // Add back to available complaint types
    setAvailableComplaintTypes(prev => [...prev, complaintType]);
  };

  const handleUpdateMapping = async () => {
    if (!selectedStaffType) {
      toastError('Please select a staff type first');
      return;
    }

    setSaving(true);
    try {
      const complaintIds = selectedComplaintTypes.map(ct => ct.type_id);
      
      await apiClient.post(`/staff/types/${selectedStaffType.type_id}/update`, {
        complaints: complaintIds
      });
      
      // Update the local staff type data
      setStaffTypes(prev => prev.map(st => 
        st.type_id === selectedStaffType.type_id 
          ? { ...st, complaints: complaintIds }
          : st
      ));
      
      // Update selected staff type
      setSelectedStaffType(prev => ({ ...prev, complaints: complaintIds }));
      
      toastSuccess('Staff-Complaint mapping updated successfully!');
    } catch (error) {
      console.error('Error updating mapping:', error);
      toastError('Failed to update mapping');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <div className="rt-title-row">
            <h2 className="rt-title">Staff - Complaint Type Mapping</h2>
          </div>
          <div className="rt-main-section">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <RTSpinner size="medium" showMessage={true} message="Loading data..." />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Staff - Complaint Type Mapping</h2>
          <div className="rt-actions">
            <RTButton
              variant="primary"
              onClick={handleUpdateMapping}
              loading={saving}
              disabled={saving || !selectedStaffType}
              style={{ marginRight: '12px' }}
            >
              {saving ? 'Updating...' : 'Update Mapping'}
            </RTButton>
            <RTButton variant="secondary" onClick={onBack}>
              ← Back
            </RTButton>
          </div>
        </div>
        
        <div className="rt-main-section">
          {/* Loading Overlay for Save Operation */}
          {saving && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999
            }}>
              <RTSpinner size="medium" showMessage={true} message="Updating staff-complaint mapping..." />
            </div>
          )}
          
          <div className="mapping-container" style={{ display: 'flex', gap: '20px', height: '600px' }}>
            
            {/* Left Side - Staff Types */}
            <div className="staff-types-panel" style={{ flex: '1', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ marginBottom: '20px', color: '#153C7F' }}>Staff Types</h3>
              
              {staffTypes.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No staff types found</p>
              ) : (
                <div 
                  className="staff-types-list"
                  style={{
                    maxHeight: '500px',
                    overflowY: 'auto',
                    paddingRight: '8px'
                  }}
                >
                  {staffTypes.map(staffType => (
                    <div
                      key={staffType.type_id}
                      className={`staff-type-item ${selectedStaffType?.type_id === staffType.type_id ? 'selected' : ''}`}
                      onClick={() => handleStaffTypeSelect(staffType)}
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedStaffType?.type_id === staffType.type_id ? '#e3f2fd' : '#fff',
                        borderColor: selectedStaffType?.type_id === staffType.type_id ? '#153C7F' : '#ddd'
                      }}
                    >
                      <div style={{ fontWeight: '500', color: '#153C7F' }}>{staffType.type_name}</div>
                      {staffType.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {staffType.description}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                        {(staffType.complaints || []).length} complaint type(s) mapped
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Complaint Types Mapping */}
            <div className="complaint-mapping-panel" style={{ flex: '1', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ marginBottom: '20px', color: '#153C7F' }}>
                {selectedStaffType ? `Complaint Types for ${selectedStaffType.type_name}` : 'Select a Staff Type'}
              </h3>
              
              {!selectedStaffType ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Please select a staff type from the left panel to manage complaint mappings.</p>
              ) : (
                <div>
                  {/* Currently Mapped Complaint Types */}
                  <div className="mapped-complaints" style={{ marginBottom: '30px' }}>
                    <h4 style={{ marginBottom: '15px', color: '#333' }}>Mapped Complaint Types:</h4>
                    
                    {selectedComplaintTypes.length === 0 ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>No complaint types mapped yet</p>
                    ) : (
                      <div className="complaint-tags">
                        {selectedComplaintTypes.map(complaintType => (
                          <div
                            key={complaintType.type_id}
                            className="complaint-tag"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #ddd',
                              borderRadius: '20px',
                              padding: '6px 12px',
                              margin: '4px',
                              fontSize: '14px'
                            }}
                          >
                            <span>{complaintType.type_name}</span>
                            <FaTrash
                              style={{
                                marginLeft: '8px',
                                cursor: 'pointer',
                                color: '#d32f2f',
                                fontSize: '12px'
                              }}
                              onClick={() => handleRemoveComplaintType(complaintType)}
                              title="Remove complaint type"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Available Complaint Types to Add */}
                  <div className="available-complaints">
                    <h4 style={{ marginBottom: '15px', color: '#333' }}>Available Complaint Types:</h4>
                    
                    {availableComplaintTypes.length === 0 ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>All complaint types are already mapped</p>
                    ) : (
                      <div 
                        className="available-complaint-list"
                        style={{
                          maxHeight: '350px',
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          padding: '8px'
                        }}
                      >
                        {availableComplaintTypes.map(complaintType => (
                          <div
                            key={complaintType.type_id}
                            className="available-complaint-item"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              border: '1px solid #eee',
                              borderRadius: '6px',
                              marginBottom: '6px',
                              backgroundColor: '#fafafa'
                            }}
                          >
                            <span>{complaintType.type_name}</span>
                            <FaPlus
                              style={{
                                cursor: 'pointer',
                                color: '#4caf50',
                                fontSize: '14px'
                              }}
                              onClick={() => handleAddComplaintType(complaintType)}
                              title="Add complaint type"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffComplaintMapping;
